/**
 * Helper standalone para detección de patrones catastrales en texto libre.
 *
 * Extraído de `CatastroGeocoder.detectPatternInText` durante la sesión
 * F1-UNIFY-MIGRATE (9-may-2026, DOP-P1=a). Lógica regex pura sin
 * dependencias de servicios — apta como pre-filtro antes de entrar a la
 * cascada Catastro V2 desde el orquestador.
 *
 * Cubre los 4 patrones más frecuentes en documentos PTEL Andalucía:
 *   1. Referencia catastral completa DGC (14 chars: 5N + 1L + 3N + 5AN).
 *   2. SIGPAC (Provincia-Municipio-Agregado-Zona-Polígono-Parcela).
 *   3. Pol/Parcela explícito ("Pol. 8 P-398", "POL 5 P.123").
 *   4. P/Pol inverso ("P-234 Pol-8", "P.45 POL.3").
 *
 * El paso 5 de la versión legacy (delegación a `CatastroService.parseCadastralText`)
 * se omite intencionadamente: aporta cobertura marginal en patrones embebidos
 * narrativos y reintroducir la dependencia anularía la ventaja del helper
 * standalone. Cualquier caso no detectado pasa al siguiente nivel de la cascada.
 *
 * @module services/geocoding/utils
 */

const RC_FULL_PATTERN = /\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5})\b/;
const SIGPAC_PATTERN = /(?:SIGPAC[:\s]*)?(\d{2})-(\d{3})-(\d)-(\d)-(\d{1,3})-(\d{1,5})/;
const POL_P_PATTERN = /POL[.\s]*(\d{1,3})\s+P[\-.\s]*(\d{1,5})/;
const P_POL_PATTERN = /P[\-.\s]*(\d{1,5})\s+POL[\-.\s]*(\d{1,3})/;

/**
 * Comprueba si un texto contiene alguno de los patrones catastrales soportados.
 *
 * @param text - Texto a analizar (case-insensitive, se normaliza a mayúsculas).
 * @returns `true` si se detecta uno de los 4 patrones; `false` en caso contrario
 *          o si `text` es nulo, indefinido o no es una cadena.
 */
export function hasCatastralPattern(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') return false;
  const normalized = text.toUpperCase().trim();
  return (
    RC_FULL_PATTERN.test(normalized) ||
    SIGPAC_PATTERN.test(normalized) ||
    POL_P_PATTERN.test(normalized) ||
    P_POL_PATTERN.test(normalized)
  );
}

/**
 * Verifica si el `name` o el `address` contienen un patrón catastral.
 *
 * Equivalente funcional de la antigua `CatastroGeocoder.hasPattern(name, address)`,
 * usada como guardia en `GeocodingOrchestrator.shouldTryCatastro` (CAT-12).
 *
 * @param name - Nombre/descripción de la infraestructura.
 * @param address - Dirección (opcional).
 * @returns `true` si alguno de los textos contiene un patrón catastral.
 */
export function hasCatastralPatternInFields(
  name: string,
  address?: string,
): boolean {
  return hasCatastralPattern(name) || hasCatastralPattern(address);
}
